import { forwardRef, memo } from "react"
import { Line } from "react-chartjs-2"
import "chart.js/auto"
import type { ChartData, ChartDataset, ChartOptions, Scale } from "chart.js"
import { Chart } from "chart.js"
import annotationPlugin, { AnnotationOptions } from "chartjs-plugin-annotation"
import zoomPlugin from "chartjs-plugin-zoom"
import {
  KernelEstimationFunction,
  color_tone2,
  color_tones,
  kde,
  kernelEpanechnikov,
  kernelGaussian,
  kernelTriangular,
  kernelUniform,
  lighter_rgba,
  smirnov,
  toCDF,
  to_rgba,
} from "@features/results/Result/const"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import {
  getAnnotations,
  getChartOptions,
  getClampCallback,
} from "@features/results/Result/ProbabilityDensityChart/helpers"
import { Typography } from "@mui/material"
import { generateChartLabel } from "@utils/chartLabelUtils"

Chart.register(annotationPlugin, zoomPlugin)

export type ProbabilityDensityChartData = {
  xValues: string[]
  yValues: number[]
  customLabel?: string
  nodeName?: string
  nodeUnit?: string
  runName?: string
  runVersion?: string
}

type ProbabilityDensityChartProps = {
  adjusted: number[][]
  adjusted2: number[][]
  data: ProbabilityDensityChartData[]
  secondYearData: [string[], number[]]
  isSecondYearSelected: boolean
  showHistograms: boolean
  showEstimations: boolean
  estimationBandwidth: number
  kernel: KernelEstimationFunction[]
  clamp: boolean
  clampStDev: number
  isCDF: boolean
  showSuprenums: boolean
  axisFormat?: string
  onZoomChange: ({ xMin, xMax }: { xMin: number; xMax: number }) => void
  maintainAspectRatio?: boolean
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export const ProbabilityDensityChart = memo(
  forwardRef(function ProbabilityDensityChart(
    {
      adjusted,
      adjusted2,
      data,
      secondYearData,
      showEstimations,
      showHistograms,
      isSecondYearSelected,
      estimationBandwidth,
      kernel,
      clamp,
      clampStDev,
      isCDF,
      showSuprenums,
      maintainAspectRatio = false,
      legendPosition = "top",
      showLegend = true,
      hiddenDatasets = [],
      onLegendClick,
      ...props
    }: ProbabilityDensityChartProps,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    chartRef: any,
  ) {
    const firstSeriesWithXValues = data.filter((x) => x.xValues.length)[0]

    if (!firstSeriesWithXValues?.xValues) {
      return <Typography>No data to show</Typography>
    }

    const numberOfRuns = data.length

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    let chartData: ChartData<any, any, string> = {
      labels: [],
      datasets: [],
    }

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    const binWidth = +firstSeriesWithXValues.xValues[1] - +firstSeriesWithXValues.xValues[0]

    const ticksCallback = function (this: Scale, value: string | number) {
      return (
        firstSeriesWithXValues.xValues[value as number] + formatValuePythonic("", props.axisFormat)
      )
    }

    const firstXYValues = {
      x: firstSeriesWithXValues.xValues,
      y: firstSeriesWithXValues.yValues,
    }
    const clampCallback = getClampCallback(adjusted, firstXYValues.x, clamp, clampStDev)

    const annotations: Record<string, AnnotationOptions> =
      data.length === 1 && !isCDF
        ? getAnnotations(
            adjusted,
            firstXYValues,
            clamp,
            estimationBandwidth,
            clampCallback,
            kernel[0],
          )
        : {}
    let options: ChartOptions<"line"> = getChartOptions({
      maintainAspectRatio,
      showEstimations,
      onZoomChange: props.onZoomChange,
      annotations,
      ticksCallback: ticksCallback,
      legendPosition,
      showLegend,
      onLegendClick,
    })

    const datasets: ChartDataset<"line", number[]>[] = []
    const pdeLabels: string[] = []

    if (showEstimations) {
      adjusted.forEach((item, index) => {
        if (kernel.includes(KernelEstimationFunction.Gaussian)) {
          const values = kde(
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            data[index].xValues,
            item,
            kernelGaussian(),
            estimationBandwidth,
            binWidth,
            clampCallback,
          )

          const dataItem = data[index]
          const kernelSuffix = isCDF ? "PDE Gaussian CDF" : "PDE Gaussian"

          const label =
            dataItem?.customLabel ||
            generateChartLabel({
              nodeName: dataItem?.nodeName || "",
              nodeUnit: dataItem?.nodeUnit,
              runName: dataItem?.runName,
              runVersion: dataItem?.runVersion,
              numberOfRuns,
              statisticalSuffix: kernelSuffix,
            })
          pdeLabels.push(label)

          datasets.push({
            label,
            data: isCDF ? toCDF(values, binWidth) : values,
            borderWidth: 0,
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            backgroundColor: to_rgba(lighter_rgba(color_tones[index], 0)),
            cubicInterpolationMode: "monotone",
            fill: true,
            stepped: false,
          })
        }

        if (kernel.includes(KernelEstimationFunction.Uniform)) {
          const values = kde(
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            data[index].xValues,
            item,
            kernelUniform(),
            estimationBandwidth,
            binWidth,
            clampCallback,
          )

          const dataItem = data[index]
          const kernelSuffix = isCDF ? "PDE Uniform CDF" : "PDE Uniform"

          const label =
            dataItem?.customLabel ||
            generateChartLabel({
              nodeName: dataItem?.nodeName || "",
              nodeUnit: dataItem?.nodeUnit,
              runName: dataItem?.runName,
              runVersion: dataItem?.runVersion,
              numberOfRuns,
              statisticalSuffix: kernelSuffix,
            })
          pdeLabels.push(label)

          datasets.push({
            label,
            data: isCDF ? toCDF(values, binWidth) : values,
            borderWidth: 0,
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            backgroundColor: to_rgba(lighter_rgba(color_tones[index], 10)),
            cubicInterpolationMode: "monotone",
            fill: true,
            stepped: false,
          })
        }

        if (kernel.includes(KernelEstimationFunction.Triangular)) {
          const values = kde(
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            data[index].xValues,
            item,
            kernelTriangular(),
            estimationBandwidth,
            binWidth,
            clampCallback,
          )

          const dataItem = data[index]
          const kernelSuffix = isCDF ? "PDE Triangular CDF" : "PDE Triangular"

          const label =
            dataItem?.customLabel ||
            generateChartLabel({
              nodeName: dataItem?.nodeName || "",
              nodeUnit: dataItem?.nodeUnit,
              runName: dataItem?.runName,
              runVersion: dataItem?.runVersion,
              numberOfRuns,
              statisticalSuffix: kernelSuffix,
            })
          pdeLabels.push(label)

          datasets.push({
            label,
            data: isCDF ? toCDF(values, binWidth) : values,
            borderWidth: 0,
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            backgroundColor: to_rgba(lighter_rgba(color_tones[index], 20)),
            cubicInterpolationMode: "monotone",
            fill: true,
            stepped: false,
          })
        }

        if (kernel.includes(KernelEstimationFunction.Epanechnikov)) {
          const values = kde(
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            data[index].xValues,
            item,
            kernelEpanechnikov(),
            estimationBandwidth,
            binWidth,
            clampCallback,
          )

          const dataItem = data[index]
          const kernelSuffix = isCDF ? "PDE Epanechnikov CDF" : "PDE Epanechnikov"
          const label =
            dataItem?.customLabel ||
            generateChartLabel({
              nodeName: dataItem?.nodeName || "",
              nodeUnit: dataItem?.nodeUnit,
              runName: dataItem?.runName,
              runVersion: dataItem?.runVersion,
              numberOfRuns,
              statisticalSuffix: kernelSuffix,
            })
          pdeLabels.push(label)

          datasets.push({
            label,
            data: isCDF ? toCDF(values, binWidth) : values,
            borderWidth: 0,
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            backgroundColor: to_rgba(lighter_rgba(color_tones[index], 30)),
            cubicInterpolationMode: "monotone",
            fill: true,
            stepped: false,
          })
        }
      })
    }

    if (showHistograms) {
      data.forEach((item, index) => {
        const historgramSuffix = isCDF ? "CDF" : undefined
        const label =
          item.customLabel ||
          generateChartLabel({
            nodeName: item.nodeName || "",
            nodeUnit: item.nodeUnit,
            runName: item.runName,
            runVersion: item.runVersion,
            numberOfRuns,
            statisticalSuffix: historgramSuffix,
          })
        datasets.push({
          label,
          data: isCDF ? toCDF(item.yValues, binWidth) : item.yValues,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          borderColor: to_rgba(lighter_rgba(color_tones[index], 0, 1)),
          borderWidth: 1,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          backgroundColor: to_rgba(lighter_rgba(color_tones[index], 0)),
          fill: true,
          stepped: "middle",
        })
      })
    }

    if (isSecondYearSelected) {
      if (showEstimations) {
        adjusted2.forEach((item) => {
          if (kernel.includes(KernelEstimationFunction.Gaussian)) {
            const values = kde(
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              data[0].xValues,
              item,
              kernelGaussian(),
              estimationBandwidth,
              binWidth,
              clampCallback,
            )

            const kernelSuffix = isCDF
              ? "PDE Gaussian (Additional Year) CDF"
              : "PDE Gaussian (Additional Year)"
            const label = generateChartLabel({
              nodeName: data[0]?.nodeName || "",
              nodeUnit: data[0]?.nodeUnit,
              numberOfRuns: 1,
              statisticalSuffix: kernelSuffix,
            })

            datasets.push({
              label,
              data: isCDF ? toCDF(values, binWidth) : values,
              borderWidth: 0,
              backgroundColor: to_rgba(lighter_rgba(color_tone2, 0)),
              cubicInterpolationMode: "monotone",
              fill: true,
              stepped: false,
            })
          }

          if (kernel.includes(KernelEstimationFunction.Uniform)) {
            const values = kde(
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              data[0].xValues,
              item,
              kernelUniform(),
              estimationBandwidth,
              binWidth,
              clampCallback,
            )

            const kernelSuffix = isCDF
              ? "PDE Uniform (Additional Year) CDF"
              : "PDE Uniform (Additional Year)"
            const label = generateChartLabel({
              nodeName: data[0]?.nodeName || "",
              nodeUnit: data[0]?.nodeUnit,
              numberOfRuns: 1,
              statisticalSuffix: kernelSuffix,
            })

            datasets.push({
              label,
              data: isCDF ? toCDF(values, binWidth) : values,
              borderWidth: 0,
              backgroundColor: to_rgba(lighter_rgba(color_tone2, 10)),
              cubicInterpolationMode: "monotone",
              fill: true,
              stepped: false,
            })
          }

          if (kernel.includes(KernelEstimationFunction.Triangular)) {
            const values = kde(
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              data[0].xValues,
              item,
              kernelTriangular(),
              estimationBandwidth,
              binWidth,
              clampCallback,
            )

            const kernelSuffix = isCDF
              ? "PDE Triangular (Additional Year) CDF"
              : "PDE Triangular (Additional Year)"
            const label = generateChartLabel({
              nodeName: data[0]?.nodeName || "",
              nodeUnit: data[0]?.nodeUnit,
              numberOfRuns: 1,
              statisticalSuffix: kernelSuffix,
            })

            datasets.push({
              label,
              data: isCDF ? toCDF(values, binWidth) : values,
              borderWidth: 0,
              backgroundColor: to_rgba(lighter_rgba(color_tone2, 20)),
              cubicInterpolationMode: "monotone",
              fill: true,
              stepped: false,
            })
          }

          if (kernel.includes(KernelEstimationFunction.Epanechnikov)) {
            const values = kde(
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              data[0].xValues,
              item,
              kernelEpanechnikov(),
              estimationBandwidth,
              binWidth,
              clampCallback,
            )

            const kernelSuffix = isCDF
              ? "PDE Epanechnikov (Additional Year) CDF"
              : "PDE Epanechnikov (Additional Year)"
            const label = generateChartLabel({
              nodeName: data[0]?.nodeName || "",
              nodeUnit: data[0]?.nodeUnit,
              numberOfRuns: 1,
              statisticalSuffix: kernelSuffix,
            })

            datasets.push({
              label,
              data: isCDF ? toCDF(values, binWidth) : values,
              borderWidth: 0,
              backgroundColor: to_rgba(lighter_rgba(color_tone2, 30)),
              cubicInterpolationMode: "monotone",
              fill: true,
              stepped: false,
            })
          }
        })
      }

      if (showHistograms) {
        const historgramSuffix = isCDF ? "Additional Year CDF" : "Additional Year"
        const label = generateChartLabel({
          nodeName: data[0]?.nodeName || "",
          nodeUnit: data[0]?.nodeUnit,
          numberOfRuns: 1,
          statisticalSuffix: historgramSuffix,
        })

        datasets.push({
          label,
          data: isCDF ? toCDF(secondYearData[1], binWidth) : secondYearData[1],
          borderColor: "rgb(111,199,91)",
          borderWidth: 1,
          backgroundColor: "rgba(111,199,91, 0.5)",
          fill: true,
          stepped: "middle",
        })
      }
    }

    if (isCDF && showSuprenums && datasets.length > 1) {
      // we need to compute the K-S between each pair
      const diffs: Record<string, AnnotationOptions<"line">> = {}
      let colorOffset = 0
      for (let i = 0; i < datasets.length - 1; i++) {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        if (pdeLabels.includes(datasets[i].label as string)) {
          for (let j = 1; j < datasets.length; j++) {
            if (
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              pdeLabels.includes(datasets[j].label as string) &&
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              datasets[i].label != datasets[j].label
            ) {
              const { suprenum, p_value, y1, y2, max_pos_x } = smirnov(
                // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                datasets[i].data,
                // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                datasets[j].data,
              )
              const content = [
                "CDF Supremum (K-S Stat) = " + suprenum.toFixed(2),
                // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                "A: " + datasets[i].label,
                // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                "B: " + datasets[j].label,
                `Fit: ${suprenum.toFixed(3)} > ${p_value.toFixed(3)} = ${suprenum > p_value}`,
              ]

              const nextColor = colorOffset++ % color_tones.length
              // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
              const color = to_rgba(color_tones[nextColor])
              const diffline: AnnotationOptions<"line"> = {
                type: "line",
                borderColor: color,
                borderDashOffset: 0,
                borderWidth: 3,
                label: {
                  display: false,
                  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                  backgroundColor: to_rgba(lighter_rgba(color_tones[nextColor], 10)),
                  color: color,
                  drawTime: "afterDatasetsDraw",
                  content: () => content,
                  //   position: "middle",
                  z: 1,
                  callout: {
                    display: true,
                    side: 10,
                  },
                },
                enter({ element }) {
                  element!.label!.options.display = true
                  return true
                },
                leave({ element }) {
                  element!.label!.options.display = false
                  return true
                },

                arrowHeads: {
                  start: { display: true },
                  end: { display: true },
                  width: 2,
                  length: 3,
                  fill: true,
                },
                xMax: max_pos_x,
                xMin: max_pos_x,
                xScaleID: "x",
                yMax: y1,
                yMin: y2,
                yScaleID: "y",
                // font: { size: 14 },
                value: () => max_pos_x,
              }
              diffs[`${i}-${j}`] = diffline
            }
          }
        }
      }

      options = getChartOptions({
        maintainAspectRatio,
        showEstimations,
        onZoomChange: props.onZoomChange,
        annotations: { ...diffs },
        ticksCallback: ticksCallback,
        showLegend,
        onLegendClick,
      })
    }

    chartData = {
      labels: firstSeriesWithXValues.xValues,
      datasets: datasets,
    }

    // Apply hidden state to datasets based on hiddenDatasets prop
    chartData.datasets.forEach((dataset) => {
      if (dataset.label) {
        dataset.hidden = hiddenDatasets.includes(dataset.label)
      }
    })

    return <Line ref={chartRef} options={options} data={chartData} />
  }),
)
